import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse} from '@angular/common/http';
import { BehaviorSubject, catchError, concatMap, filter, finalize, Observable, retry, take, throwError, timer } from 'rxjs';
import { AuthenticationService } from '../../services/AuthenticationService';
import { AppConfig } from '../../app.config';
import { Router } from '@angular/router';
import { Tokens } from '../../models/tokens';
import { SKIP_AUTH_INTERCEPTOR } from './http-context-tokens';
 
export const retryCount = 4;
export const retryBaseDelayMs = 500;
export const retryMaxDelayMs = 8000;
 
// Three explicit states instead of an overloaded boolean:
// - Pending: a refresh call is currently in flight
// - Success: the in-flight refresh completed successfully
// - Failed:  the in-flight refresh completed with an error
enum TokenRefreshStatus {
    Pending = 'PENDING',
    Success = 'SUCCESS',
    Failed = 'FAILED'
}
 
@Injectable()
export class AppInterceptor implements HttpInterceptor {
 
    isRefreshingToken = false;
    tokenRefreshedSubject = new BehaviorSubject<TokenRefreshStatus>(TokenRefreshStatus.Success);
 
    constructor(private authService:AuthenticationService, private appConfig:AppConfig, private router:Router) {}
 
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (request.url.endsWith("/login") || request.url.endsWith("/refresh") || request.context.get(SKIP_AUTH_INTERCEPTOR)) {
            return next.handle(request);
        }
        else{
            return next.handle(request).pipe(
                catchError(error => {
                    if (this.checkTokenExpiryErr(error)) {
                        return this.handle401Error(request, next);
                    }
                    return throwError(()=>(error));
                })
            );
        }
    }
 
    private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
        if (this.isRefreshingToken) {
            // Wait for the in-flight refresh to settle, one way or the other.
            // Filtering out "Pending" means we never resolve on the transient
            // in-progress state, and we never hang forever either, since
            // Success/Failed are always eventually emitted in the finalize below.
            return this.tokenRefreshedSubject.pipe(
                filter(status => status !== TokenRefreshStatus.Pending),
                take(1),
                concatMap(status => {
                    if (status === TokenRefreshStatus.Success) {
                        return next.handle(this.updateHeader(req));
                    }
                    return throwError(() => new Error('Token refresh failed'));
                })
            );
        }
 
        this.isRefreshingToken = true;
        this.tokenRefreshedSubject.next(TokenRefreshStatus.Pending);
 
        return this.authService.refreshTokens().pipe(
            retry({
                count: retryCount,
                delay: (error, retryIndex) => {
                    // Don't retry a genuinely invalid refresh token - that will
                    // never succeed no matter how many times we ask.
                    if (error instanceof HttpErrorResponse && !this.isRetryableError(error)) {
                        throw error;
                    }
                    const backoff = Math.min(retryBaseDelayMs * Math.pow(2, retryIndex - 1), retryMaxDelayMs);
                    const jitter = Math.random() * 0.3 * backoff; // ±30% jitter
                    return timer(backoff + jitter);
                }
            }),
            concatMap((res: Tokens) => {
                this.tokenRefreshedSubject.next(TokenRefreshStatus.Success);
                return next.handle(this.updateHeader(req));
            }),
            catchError((error) => {
                // Unblock any requests queued behind this refresh, regardless
                // of outcome, so they fail fast instead of hanging.
                this.tokenRefreshedSubject.next(TokenRefreshStatus.Failed);
 
                // Only tear down the session on a genuinely invalid refresh
                // token. A transient failure (network blip, 5xx, timeout)
                // should not log the user out from under them.
                if (error instanceof HttpErrorResponse && this.checkInvalidAttemptErr(error)) {
                    this.authService.logout();
                } else {
                    this.router.navigate(['/error']);
                }
 
                return throwError(()=>(error));
            }),
            finalize(() => {
                this.isRefreshingToken = false;
            })
        );
    }

    private isRetryableError(error: HttpErrorResponse): boolean {
        return error.status === 0 ||               // network failure / no response
            error.status === 500 ||
            error.status === 502 ||
            error.status === 503 ||
            error.status === 504;
    }
 
    private checkTokenExpiryErr(error: HttpErrorResponse): boolean {
        return error.status === 401 && error.headers.has('is-token-expired');
    }
 
    private checkInvalidAttemptErr(error: HttpErrorResponse): boolean {
        return (
            error.status &&
            error.status === 401 &&
            error.error.startsWith('Invalid attempt!')
        );
    }
 
    updateHeader(request:any) {
        if (this.appConfig.authHeader) {
            return request.clone({
                setHeaders: {
                    "Authorization": this.appConfig.authHeader
                }
            });
        }
        return request;
    }
 
    logout() {
        this.router.navigate(['/login']);
        this.authService.logout();
    }
}